import { useState } from 'react';
import { useConnections } from '../hooks/useConnections';
import CompanyDirectory from '../components/inventor/CompanyDirectory';
import NDASignature from '../components/inventor/NDASignature';
import { fetchApi } from '../lib/api';

export default function CompaniesPage() {
  const { approved, loading } = useConnections();
  const [ndaView, setNdaView] = useState(null); // { connection, companyName, documentUrl, ndaVersion }

  // Check if a connected company needs NDA signing
  const startNDA = async (connection) => {
    try {
      // Get company profile to find NDA document
      const dirData = await fetchApi('/api/companies/directory');
      const company = (dirData.companies || []).find(c => c.id === connection.company_id);

      // We need to get the full company profile for NDA details
      // The directory doesn't expose NDA info, so check NDA records
      const ndaData = await fetchApi(`/api/nda/${connection.id}`);
      const records = ndaData.records || [];
      const alreadySigned = records.some(r => r.inventor_signed_at);

      if (alreadySigned) {
        alert('You have already signed the NDA for this company. Waiting for company action.');
        return;
      }

      // For now, we'll need the company's NDA URL from the connection context
      // This would normally come from the company_profiles table
      setNdaView({
        connection,
        companyName: company?.company_name || connection.company?.company_name || 'Company',
        documentUrl: connection.company?.nda_document_url || null,
        ndaVersion: connection.company?.nda_version || 1,
      });
    } catch (err) {
      alert('Failed to start NDA flow: ' + err.message);
    }
  };

  if (ndaView) {
    return (
      <div>
        <button
          onClick={() => setNdaView(null)}
          className="text-vault-gold hover:text-vault-gold-dark text-sm font-semibold mb-4"
        >
          ← Back to Companies
        </button>
        <NDASignature
          connection={ndaView.connection}
          companyName={ndaView.companyName}
          documentUrl={ndaView.documentUrl}
          ndaVersion={ndaView.ndaVersion}
          onComplete={() => setNdaView(null)}
        />
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-heading text-3xl text-vault-navy mb-6">Companies</h1>

      {/* Connected companies with NDA status */}
      {!loading && approved.length > 0 && (
        <div className="mb-8">
          <h2 className="text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Your Connections</h2>
          <div className="space-y-3 mb-8">
            {approved.map(conn => (
              <div key={conn.id} className="glass p-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {conn.company?.logo_url ? (
                    <img src={conn.company.logo_url} alt="" className="w-10 h-10 rounded-btn object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-btn bg-gradient-to-br from-vault-gold to-amber-500 flex items-center justify-center text-white font-bold text-sm">
                      {conn.company?.company_name?.charAt(0) || '?'}
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-vault-navy text-sm">{conn.company?.company_name}</div>
                    <div className="flex items-center gap-2 mt-1">
                      {conn.nda_agreed ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> NDA Signed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-medium">
                          NDA Required
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {!conn.nda_agreed && (
                  <button
                    onClick={() => startNDA(conn)}
                    className="px-4 py-2 rounded-btn text-sm font-semibold text-white"
                    style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
                  >
                    Sign NDA
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Directory */}
      <CompanyDirectory />
    </div>
  );
}
